import type { Melody } from '@tunasong/schemas'
import { GRID_TOLERANCE, isOnGrid, maxOffsetMs } from './grid.js'

/**
 * The §2 invariants, checked. Zod cannot express them: they are relations between notes and
 * between a note and the melody's tempo/grid, not per-field bounds.
 *
 * Returns every issue rather than throwing on the first - the editor wants to show a list, and a
 * capture that lands two problems on one note should say so.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §2
 */

export type MelodyIssueKind = 'off-grid' | 'unordered' | 'overlap' | 'offset-out-of-bounds' | 'non-positive-duration'

export interface MelodyIssue {
  kind: MelodyIssueKind
  /** Index into `melody.notes`. For 'unordered' and 'overlap', the later of the two notes. */
  noteIndex: number
  message: string
}

export const validateMelody = (melody: Melody): MelodyIssue[] => {
  const { notes, grid, tempo } = melody
  const bound = maxOffsetMs(tempo, grid)
  const issues: MelodyIssue[] = []

  notes.forEach((note, index) => {
    if (!isOnGrid(note.onset, grid)) {
      issues.push({
        kind: 'off-grid',
        noteIndex: index,
        message: `Onset ${note.onset} is not on the ${grid} grid`,
      })
    }
    if (note.duration <= 0) {
      issues.push({
        kind: 'non-positive-duration',
        noteIndex: index,
        message: `Duration ${note.duration} is not greater than zero`,
      })
    }
    if (Math.abs(note.offsetMs) > bound + GRID_TOLERANCE) {
      issues.push({
        kind: 'offset-out-of-bounds',
        noteIndex: index,
        message: `Offset ${note.offsetMs} ms exceeds half a ${grid} slot (${bound} ms) at ${tempo.bpm} bpm`,
      })
    }

    const previous = notes[index - 1]
    if (!previous) {
      return
    }
    if (note.onset < previous.onset - GRID_TOLERANCE) {
      issues.push({
        kind: 'unordered',
        noteIndex: index,
        message: `Onset ${note.onset} precedes the previous note at ${previous.onset}`,
      })
      return
    }
    if (note.onset < previous.onset + previous.duration - GRID_TOLERANCE) {
      issues.push({
        kind: 'overlap',
        noteIndex: index,
        message: `Onset ${note.onset} falls inside the previous note span`,
      })
    }
  })

  return issues
}
